import type { IStringTool } from "../core/IStringTool.ts"

/**
 * Input parameters for calculating magnetic pull factor.
 */
interface MagneticPullInput {
  /** Distance between pointer and element center (px). */
  distance: number

  /** Max distance within which magnetic pull is active. */
  radius: number

  /** Strength of the magnetic pull (0–1 recommended). */
  strength: number
}

/**
 * Output: factor to multiply by direction vector (dx/dy) to get magnetic offset.
 */
type MagneticPullOutput = number

/**
 * Tool for calculating magnetic attraction based on distance to element.
 * Returns a scalar value (0..strength) depending on proximity.
 */
export default class MagneticPullTool implements IStringTool<MagneticPullInput, MagneticPullOutput> {
  /**
   * Returns a pull factor based on distance to target within a radius.
   * @param input - Magnetic pull parameters.
   * @returns A multiplier (typically < 1) to apply to dx/dy.
   */
  process({ distance, radius, strength }: MagneticPullInput): number {
    if (distance >= radius) return 0
    const proximity = (radius - distance) / radius // 1 when close, 0 at edge
    return strength * proximity
  }
}
